import { TCampaignSummary } from "domain/entities/campaign-summary-response";
import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { checkParamsPortfolioOnly } from "helper/filtersHelper";

type TCampaignSummaryProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TCampaignSummaryState = {
  requestParamsIndex: number;
  campaignSummaryData?: TCampaignSummary;
  downloadingReports?: boolean;
  downloadCampaignSummaryReport: () => void;
  isLoading: boolean;
};

const initContext: TCampaignSummaryState = {
  requestParamsIndex: 0,
  downloadCampaignSummaryReport: () => null,
  isLoading: true,
};
export const CampaignSummaryContext = createContext(initContext);

export const CampaignSummaryProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TCampaignSummaryProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [campaignSummaryData, setCampaignSummaryData] = useState<any>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadCampaignSummaryReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportCampaignSummaryCSV(currentRequestParams)
      .then(createBlobDownloader("campaign-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsPortfolioOnly);
    }

    return checkParamsPortfolioOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getCampaignSummary(currentRequestParams)
        .then(setCampaignSummaryData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <CampaignSummaryContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        campaignSummaryData,
        downloadCampaignSummaryReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </CampaignSummaryContext.Provider>
  );
};
